import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import { SendConfirmPanel } from './SendConfirmPanel';
import { SendSuccessPanel } from './SendSuccessPanel';
import { ProgressStages } from '../ProgressStages';
import type { BorrowerPair, GenerationStage, SendOptions } from '../../types/disclosures';
type Phase = 'confirm' | 'sending' | 'success';
const sendingLabels = ['Package prepared', 'Recipients verified', 'Sending disclosures'];
interface SendDrawerProps {
  open: boolean;
  pair: BorrowerPair;
  documentCount: number;
  onClose: () => void;
  onSent: (options: SendOptions) => void;
  onViewHistory: () => void;
}
export function SendDrawer({
  open,
  pair,
  documentCount,
  onClose,
  onSent,
  onViewHistory
}: SendDrawerProps) {
  const [phase, setPhase] = useState<Phase>('confirm');
  const [options, setOptions] = useState<SendOptions | null>(null);
  const [stages, setStages] = useState<GenerationStage[]>([]);
  const timers = useRef<number[]>([]);
  useEffect(() => {
    if (open) {
      setPhase('confirm');
      setOptions(null);
      setStages([]);
    }
  }, [open]);
  useEffect(() => () => {
    timers.current.forEach(id => window.clearTimeout(id));
    timers.current = [];
  }, []);
  const startSend = (nextOptions: SendOptions) => {
    if (phase !== 'confirm') return;
    setOptions(nextOptions);
    setPhase('sending');
    const advance = (index: number) => setStages(sendingLabels.map((label, i) => ({
      id: label,
      label,
      state: i < index ? 'done' : i === index ? 'active' : 'pending'
    })));
    advance(0);
    timers.current.push(window.setTimeout(() => advance(1), 800));
    timers.current.push(window.setTimeout(() => advance(2), 1600));
    timers.current.push(window.setTimeout(() => {
      setStages(sendingLabels.map(label => ({
        id: label,
        label,
        state: 'done' as const
      })));
      setPhase('success');
      onSent(nextOptions);
    }, 2600));
  };
  return <AnimatePresence data-id="element-358" data-mp="qtx0adkxx28k">
      {open && <div className="fixed inset-0 z-50 flex justify-end" data-id="element-359" data-mp="quya6eq7ziop">
          <motion.div className="absolute inset-0 bg-slate-900/30" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={phase === 'sending' ? undefined : onClose} aria-hidden="true" data-id="element-360" data-mp="lukdfsu2jv5z" />
          <motion.div role="dialog" aria-modal="true" aria-labelledby="send-drawer-title" initial={{
        x: 32,
        opacity: 0
      }} animate={{
        x: 0,
        opacity: 1
      }} exit={{
        x: 32,
        opacity: 0
      }} transition={{
        type: 'spring',
        stiffness: 380,
        damping: 34
      }} className="relative flex h-full w-full max-w-md flex-col border-l border-slate-200 bg-white shadow-pop" data-id="element-361" data-mp="75xm0sr63cye">
            <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-6 py-4" data-id="element-362" data-mp="3kf2d9jtflpcn">
              <div data-id="element-363" data-mp="3abex57ghtxhh">
                <h2 id="send-drawer-title" className="text-[15px] font-semibold text-slate-900" data-id="element-364" data-mp="3f7vjpx81o3sg">
                  {phase === 'sending' ? 'Sending Initial Disclosures' : 'Send Initial Disclosures'}
                </h2>
                <p className="mt-0.5 text-[13px] text-slate-500" data-id="element-365" data-mp="3p6l3jvji3ybc">
                  {phase === 'confirm' ? 'Confirm who should receive this package.' : pair.label}
                </p>
              </div>
              {phase !== 'sending' && <button type="button" onClick={onClose} className="rounded-md p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500" aria-label="Close" data-id="element-366" data-mp="31h0h32o52ixb">
                  <XIcon className="h-4 w-4" data-id="element-367" data-mp="2dq4nc004hdnf" />
                </button>}
            </div>

            {phase === 'confirm' && <SendConfirmPanel pair={pair} documentCount={documentCount} onCancel={onClose} onSubmit={startSend} data-id="element-368" data-mp="j8lazgrl6d0f" />}

            {phase === 'sending' && <div className="flex-1 px-6 py-8" data-id="element-369" data-mp="o8zkl38u5v0k">
                <ProgressStages stages={stages} data-id="element-370" data-mp="o87ozsf8nxaa" />
                <p className="mt-6 text-[13px] text-slate-500" data-id="element-371" data-mp="120e6bhu4oabb">
                  Please keep this panel open — this normally completes shortly.
                </p>
              </div>}

            {phase === 'success' && options && <SendSuccessPanel pair={pair} options={options} documentCount={documentCount} onDone={onClose} onViewHistory={onViewHistory} data-id="element-372" data-mp="1bx90jojdbojs" />}
          </motion.div>
        </div>}
    </AnimatePresence>;
}